import { getRegion } from '@m2h/common/utils';
import {
  PassportSwitchAuthResult,
  switchPassportAuth,
} from '@m2h/common/webApi/passport';
import { SUPPORT_REGIONS } from '@sailor/compass';

interface CheckSwitchAuthParams {
  switchRegion?: string;
}

type CheckSwitchAuthCallback = (result: PassportSwitchAuthResult) => void;

interface InFlightSwitchAuth {
  key: string;
  promise: Promise<PassportSwitchAuthResult>;
}

let inFlightSwitchAuth: InFlightSwitchAuth | undefined;

const SUPPORT_REGION_SET = new Set(SUPPORT_REGIONS);
const normalizeRegion = (region?: string): string => {
  if (typeof region !== 'string') return '';
  const normalizedRegion = region.trim().toUpperCase();
  return SUPPORT_REGION_SET.has(normalizedRegion) ? normalizedRegion : '';
};

const invalidRegionResult = (
  code: 'INVALID_SWITCH_REGION' | 'INVALID_SOURCE_REGION' = 'INVALID_SWITCH_REGION',
): PassportSwitchAuthResult => ({
  success: false,
  error: {
    code,
    type: code,
    message: code === 'INVALID_SOURCE_REGION'
      ? 'Current Region is not supported'
      : 'switchRegion is not supported',
  },
});

const busyResult = (): PassportSwitchAuthResult => ({
  success: false,
  error: {
    code: 'SWITCH_AUTH_BUSY',
    type: 'SWITCH_AUTH_BUSY',
    message: 'Another region switch authorization is in progress',
  },
});

const unexpectedFailureResult = (error: unknown): PassportSwitchAuthResult => ({
  success: false,
  error: {
    code: 'SWITCH_AUTH_FAILED',
    type: 'SWITCH_AUTH_FAILED',
    message: error instanceof Error && error.message
      ? error.message
      : 'Failed to switch account authorization',
  },
});

const attachCallback = (
  promise: Promise<PassportSwitchAuthResult>,
  callback?: CheckSwitchAuthCallback,
) => {
  if (typeof callback !== 'function') return;

  let callbackCalled = false;
  promise.then((result) => {
    if (callbackCalled) return;
    callbackCalled = true;
    try {
      callback(result);
    } catch (error) {
      console.error('[SOAAccount] checkSwitchAuth callback failed', error);
    }
  });
};

const returnResult = (
  promise: Promise<PassportSwitchAuthResult>,
  callback?: CheckSwitchAuthCallback,
): Promise<PassportSwitchAuthResult> => {
  attachCallback(promise, callback);
  return promise;
};

export const checkSwitchAuth = (
  params?: CheckSwitchAuthParams,
  callback?: CheckSwitchAuthCallback,
): Promise<PassportSwitchAuthResult> => {
  const switchRegion = normalizeRegion(params?.switchRegion);
  if (!switchRegion) {
    return returnResult(Promise.resolve(invalidRegionResult()), callback);
  }

  // sourceRegion 必须在请求前从 C 端现有 Region 链路获取快照。
  const sourceRegion = normalizeRegion(getRegion());
  if (!sourceRegion) {
    return returnResult(Promise.resolve(invalidRegionResult('INVALID_SOURCE_REGION')), callback);
  }
  const requestKey = `${sourceRegion}->${switchRegion}`;
  if (inFlightSwitchAuth) {
    if (inFlightSwitchAuth.key === requestKey) {
      return returnResult(inFlightSwitchAuth.promise, callback);
    }
    return returnResult(Promise.resolve(busyResult()), callback);
  }

  if (sourceRegion === switchRegion) {
    return returnResult(Promise.resolve({
      success: true,
      data: { status: 1 },
    }), callback);
  }

  const requestPromise = Promise.resolve()
    .then(() => switchPassportAuth({ sourceRegion, switchRegion }))
    .catch(unexpectedFailureResult);

  inFlightSwitchAuth = {
    key: requestKey,
    promise: requestPromise,
  };

  requestPromise.finally(() => {
    if (inFlightSwitchAuth?.promise === requestPromise) {
      inFlightSwitchAuth = undefined;
    }
  });

  return returnResult(requestPromise, callback);
};

const SOAAccount = {
  checkSwitchAuth,
};

export default SOAAccount;
